import { FC, useEffect, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { Text } from '../components/Text';
import { InlineLoadError } from '../components/InlineLoadError';
import { LockIcon } from '../theme/Icons';
import { AnimatedCollapse } from '../components/AnimatedCollapse';
import { ChevronButton } from '../components/ChevronButton';
import { useAsyncEffect } from '../support/useAsyncEffect';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { MemoriesModel } from './MemoriesModel';
import { IMemory, MemoryCategory } from './IMemory';
import { formatMemorySourceLine } from './formatMemorySourceLine';
import { MEMORY_CATEGORY_LABELS } from './memoryCategoryLabels';
import { ViewMemoryWithQueryParamDrawer } from './ViewMemoryWithQueryParamDrawer';

const useStyles = createUseStyles({
    toggle: {
        display: 'flex',
        alignItems: 'center',
        // chevron sits just right of the title (not pushed to the far edge) so it reads as part of
        // the label and its expandability is obvious
        gap: 8,
        cursor: 'pointer',
    },

    header: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 4,
        marginTop: 8,
        marginBottom: 14,
    },

    lockNote: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 13,
        color: 'var(--ion-color-step-600)',
        marginTop: 2,
    },

    rows: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 14,
    },

    categoryGroup: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 10,
    },

    row: {
        background: 'var(--ion-color-step-50)',
        borderRadius: 12,
        padding: '10px 14px',
        cursor: 'pointer',
    },

    fact: {
        fontSize: 15,
        lineHeight: '1.5',
        color: 'var(--ion-text-color)',
    },

    sourceLine: {
        fontSize: 12,
        color: 'var(--ion-color-step-600)',
        marginTop: 4,
    },
});

export interface IMemoryListProps {
    model: MemoriesModel;

    // Consumers that host the list inside a modal (Account Settings) inject their own handler so the
    // detail renders in place rather than opening the standalone drawer behind the modal. Omitted on
    // the care profile, where the default drawer has nothing stacked above it.
    onOpenMemory?: (memory: IMemory) => void;
}

export const MemoryList: FC<IMemoryListProps> = ({ model, onOpenMemory }) => {
    const classes = useStyles();
    useSubscription(() => model);

    const tracking = useInstance(MixPanelEventEmitter);
    const viewMemoryDrawer = useInstance(ViewMemoryWithQueryParamDrawer);

    const [isOpen, setIsOpen] = useState(false);
    const hasTrackedView = useRef(false);

    // Collapse state and the one-per-list "viewed" tracking both reset when the list switches to
    // another care-recipient's model, so each distinct list opens collapsed and re-emits on expand.
    useEffect(() => {
        setIsOpen(false);
        hasTrackedView.current = false;
    }, [model]);

    useAsyncEffect(async () => {
        await model.ensureLoaded();
    }, [model]);

    // Collapsed by default, so mount alone doesn't mean the memories were seen -- fire the
    // one-per-list view event on the first expand instead.
    const onToggle = () => {
        const nextOpen = !isOpen;
        setIsOpen(nextOpen);
        if (nextOpen && !hasTrackedView.current) {
            hasTrackedView.current = true;
            tracking.track('Memory List Viewed', () => ({ scope: model.scope }));
        }
    };

    // A failed initial fetch (distinct from "loaded but empty" or "no access") gets an inline retry
    // so the section isn't just silently absent when the load didn't succeed.
    if (model.loadFailed) {
        return (
            <div data-id="memory-list" data-type="memory-list" data-scope={model.scope}>
                <Text type="subtitle">What Bea remembers</Text>
                <InlineLoadError
                    dataId="memory-list-load-error"
                    message="Couldn't load what Bea remembers. Check your connection and try again."
                    onRetry={() => model.ensureLoaded()}
                />
            </div>
        );
    }

    // Viewers without get-private access on a care-recipient scope aren't returned the memories
    // link at all -- hide the whole section rather than show an empty transparency message that
    // would misleadingly imply Bea has nothing recorded.
    if (!model.hasLoaded || !model.hasAccess()) {
        return null;
    }

    const memories = model.getMemories();

    const handleOpenMemory = onOpenMemory ?? ((memory: IMemory) => {
        viewMemoryDrawer.open({ memoryId: memory.memoryId, scope: model.scope });
    });

    return (
        <div data-id="memory-list" data-type="memory-list" data-scope={model.scope} data-expanded={String(isOpen)}>
            <div className={classes.toggle} data-id="memory-list-toggle" onClick={onToggle}>
                <Text type="subtitle">What Bea remembers</Text>
                <ChevronButton direction={isOpen ? 'up' : 'down'} />
            </div>

            <AnimatedCollapse collapsed={!isOpen}>
                <div className={classes.header}>
                    <Text type="detail">
                        Bea keeps notes like these from your recordings and conversations, so she can offer more personalized support. You can review, edit, or delete anything below.
                    </Text>
                    {model.scope === 'personal' && (
                        <div className={classes.lockNote} data-id="memory-list-personal-lock">
                            <LockIcon color="medium" size="small" />
                            <span>Only you can see this</span>
                        </div>
                    )}
                </div>

                {memories.length === 0 ? (
                    <Text type="detail" data-type="memory-list-empty">
                        Nothing yet. As you talk with Bea, what she learns will show up here.
                    </Text>
                ) : (
                    <MemoryRows memories={memories} onOpenMemory={handleOpenMemory} />
                )}
            </AnimatedCollapse>
        </div>
    );
};

// The server already sorts observations by category, so grouping here just needs to notice
// category boundaries as they appear, not re-sort.
const groupObservationsByCategory = (observations: IMemory[]): { category: MemoryCategory; memories: IMemory[] }[] => {
    const categoryOrder: MemoryCategory[] = [];
    const byCategory = new Map<MemoryCategory, IMemory[]>();
    for (const memory of observations) {
        if (!byCategory.has(memory.category)) {
            categoryOrder.push(memory.category);
            byCategory.set(memory.category, []);
        }
        byCategory.get(memory.category)!.push(memory);
    }
    return categoryOrder.map(category => ({ category, memories: byCategory.get(category)! }));
};

interface IMemoryRowsProps {
    memories: IMemory[];
    onOpenMemory: (memory: IMemory) => void;
}

const MemoryRows: FC<IMemoryRowsProps> = ({ memories, onOpenMemory }) => {
    const classes = useStyles();

    const synthesized = memories.filter(memory => memory.level === 'synthesized');
    const observations = memories.filter(memory => memory.level !== 'synthesized');
    const categoryGroups = groupObservationsByCategory(observations);

    return (
        <div className={classes.rows}>
            {synthesized.length > 0 && (
                <div className={classes.categoryGroup} data-id="memory-general-group">
                    <Text type="detail">General</Text>
                    {synthesized.map(memory => (
                        <MemoryRow key={memory.memoryId} memory={memory} onClick={() => onOpenMemory(memory)} />
                    ))}
                </div>
            )}

            {categoryGroups.map(({ category, memories: categoryMemories }) => (
                <div key={category} className={classes.categoryGroup} data-id="memory-category-group" data-category={category}>
                    <Text type="detail">{MEMORY_CATEGORY_LABELS[category]}</Text>
                    {categoryMemories.map(memory => (
                        <MemoryRow key={memory.memoryId} memory={memory} onClick={() => onOpenMemory(memory)} />
                    ))}
                </div>
            ))}
        </div>
    );
};

interface IMemoryRowProps {
    memory: IMemory;
    onClick: () => void;
}

const MemoryRow: FC<IMemoryRowProps> = ({ memory, onClick }) => {
    const classes = useStyles();

    return (
        <div
            className={classes.row}
            data-id="memory-row"
            data-type="memory-row"
            data-memory-id={memory.memoryId}
            onClick={onClick}
        >
            <div className={`${classes.fact} mp-mask`}>{memory.fact}</div>
            <div className={classes.sourceLine}>{formatMemorySourceLine(memory)}</div>
        </div>
    );
};
